import { useContext, useEffect, useRef } from "react"
import { useShallow } from "zustand/react/shallow"

import { CardUserUI } from "./ui"
import { FieldsErrorType, ICardUserContainer, ICardUserUI, IFieldsError } from "./types"

import { useUsers } from "@api/users/state"
import { CardUserAccessType, CardUserSitesType } from "@api/users/state.types"

import { useValue } from "@hooks/useValue"
import { useCardSaveGuard } from '@hooks/useCardSaveGuard'
import { useFieldsErrorAnimation } from "@hooks/useFieldsErrorAnimation"
import { validFormData } from "@hooks/useForm"

import { CardModeType } from "@smart/common.type"
import { DictNameType } from "@smart/dictSelector/types"
import { IModalConfirm, ModalConfirm } from "@primitive/modal.confirm"

import { NotificationContext, notifyOpen } from "@ui/notification/notification.provider"


type UserFormState = {
    loginName: string
    loginPassword: string
    lastName: string
    firstName: string
    secondName: string
    accessLevelID: number
    isLocked: number
}

const EMPTY_USER_FORM_STATE: UserFormState = {
    loginName: '',
    loginPassword: '',
    lastName: '',
    firstName: '',
    secondName: '',
    accessLevelID: 0,
    isLocked: 0,
}

const createUserFormSnapshot = (state: UserFormState) => JSON.stringify(state)

/** Контейнер анкета - Операторы */
export const CardUserContainer = ({isOpen, cardMode, cardID, handleButtonOk, handleButtonCancel}: ICardUserContainer) => {
    const notify = useContext(NotificationContext)

    const currentCardID = useValue<number>(0)
    const currentCardMode = useValue<CardModeType | ''>('')

    /** Под ошибки для полей */
    const fieldsError = useValue<IFieldsError>({})
    /** Активность кнопки сохранить */
    const isDisableBtnSave = useValue<boolean>(true)

    /** Поле логин */
    const txtLoginName = useValue<string>('')
    /** Поле пароль */
    const txtLoginPassword = useValue<string>('')
    /** Поле фамилия */
    const txtLastName = useValue<string>('')
    /** Поле имя */
    const txtFirstName = useValue<string>('')
    /** Поле отчество */
    const txtSecondName = useValue<string>('')
    /** Идентификатор должности */
    const txtAccessLevelID = useValue<number>(0)
    /** Поле наименование должности */
    const txtAccessLevelName = useValue<string>('')
    /** Поле блокировка */
    const txtIsLocked = useValue<number>(0)
    
    /** Список доступных сайтов */
    const itemsSites = useValue<CardUserSitesType[]>([])
    /** Список доступных разделов */
    const itemsAccess = useValue<CardUserAccessType[]>([])

    /** Окно подтверждения */
    const modalConfirm = useValue<IModalConfirm>({isOpen: false})
    const savedFormSnapshotRef = useRef(createUserFormSnapshot(EMPTY_USER_FORM_STATE))
    const hasSavedChangesRef = useRef(false)
    const saveGuard = useCardSaveGuard(isOpen, `${cardMode}:${cardID}`, () => {
        hasSavedChangesRef.current = true
        notify && notifyOpen('Результат сохранения не подтверждён. Анкета могла быть создана. Повторное создание заблокировано; закройте анкету и проверьте список.', 'warning', 10000, notify)
    })
    const isFormReadyRef = useRef(false)
    

    const { // Подключаемся к хранилищу Пользователи
        isLoadingCard, 
        isLoadingCheckedSectionRead,
        isLoadingCheckedSectionEdit,
        isLoadingCheckedSectionDelete,
        
        clearStateUserCard, actionGetUserCard, actionPostUserCard, actionPutUserCard,
        actionPostUserAccessSites, actionPostUserAccessSections,
        actionDeleteUserAccessSites, actionDeleteUserAccessSections,
        actionPutUserAccessSectionsChecked
    } = useUsers(useShallow((state) => ({
        isLoadingCard: state.isLoadingCard,
        isLoadingCheckedSectionRead: state.isLoadingCheckedSectionRead,
        isLoadingCheckedSectionEdit: state.isLoadingCheckedSectionEdit,
        isLoadingCheckedSectionDelete: state.isLoadingCheckedSectionDelete,

        clearStateUserCard: state.clearStateUserCard,
        actionGetUserCard: state.actionGetUserCard,
        actionPostUserCard: state.actionPostUserCard,
        actionPutUserCard: state.actionPutUserCard,
        actionPostUserAccessSites: state.actionPostUserAccessSites,
        actionPostUserAccessSections: state.actionPostUserAccessSections,
        actionDeleteUserAccessSites: state.actionDeleteUserAccessSites,
        actionDeleteUserAccessSections: state.actionDeleteUserAccessSections,
        actionPutUserAccessSectionsChecked: state.actionPutUserAccessSectionsChecked,
    })))

    const canEdit = useUsers(state => state.users.canRead && state.users.canEdit && (currentCardMode.value !== "edit" || state.userCard.canEdit))
    const canEditCurrentCard = () => {
        const state = useUsers.getState()
        return state.users.canRead && state.users.canEdit && (currentCardMode.value !== "edit" || state.userCard.canEdit)
    }

    const clearCard = () => {
        clearStateUserCard()
        currentCardID.setValue(0)
        currentCardMode.setValue('new')
        txtLoginName.setValue('')
        txtLoginPassword.setValue('')
        txtLastName.setValue('')
        txtFirstName.setValue('')
        txtSecondName.setValue('')
        txtAccessLevelID.setValue(0)
        txtAccessLevelName.setValue('')
        txtIsLocked.setValue(0)
        itemsSites.setValue([])
        itemsAccess.setValue([])
        isDisableBtnSave.setValue(true)
        isFormReadyRef.current = false
        hasSavedChangesRef.current = false
        savedFormSnapshotRef.current = createUserFormSnapshot(EMPTY_USER_FORM_STATE)
    }

    const getCurrentFormState = (): UserFormState => ({
        loginName: txtLoginName.value,
        loginPassword: txtLoginPassword.value,
        lastName: txtLastName.value,
        firstName: txtFirstName.value,
        secondName: txtSecondName.value,
        accessLevelID: txtAccessLevelID.value,
        isLocked: txtIsLocked.value,
    })

    //==================================================================================

    useEffect(() => {
        let isCurrentRequest = true
        const requestedCardID = Number(cardID)
        const requestedCardMode = cardMode ?? 'new'

        clearCard()

        if (isOpen) {
            currentCardID.setValue(requestedCardID)
            currentCardMode.setValue(requestedCardMode)
            if (requestedCardMode === 'edit' && requestedCardID > 0) {
                loadCardData(requestedCardID, () => isCurrentRequest)
            } else {
                isFormReadyRef.current = true
            }
        }

        return () => {
            isCurrentRequest = false
        }
    }, [isOpen, cardID, cardMode]) // eslint-disable-line react-hooks/exhaustive-deps

    const currentFormSnapshot = createUserFormSnapshot(getCurrentFormState())
    const setIsDisableBtnSave = isDisableBtnSave.setValue

    useEffect(() => {
        if (!isOpen || !isFormReadyRef.current) {
            setIsDisableBtnSave(true)
            return
        }

        setIsDisableBtnSave(currentFormSnapshot === savedFormSnapshotRef.current)
    }, [isOpen, currentFormSnapshot, setIsDisableBtnSave])

    // ================================================================================

    /** Загрузка данных по анкете */
    const loadCardData = (loginID: number, isCurrentRequest: () => boolean = () => true) => {
        actionGetUserCard(loginID).then((res) => {
            if (!isCurrentRequest()) return
            if (res.status === 403) return notify && notifyOpen(res.message, 'error', 2000, notify)

            if (res.status === 200) {
                const formState: UserFormState = {
                    loginName: res.data?.loginName || '',
                    loginPassword: '',
                    lastName: res.data?.lastName || '',
                    firstName: res.data?.firstName || '',
                    secondName: res.data?.secondName || '',
                    accessLevelID: res.data?.accessLevelID || 0,
                    isLocked: res.data?.isLocked || 0,
                }

                txtLoginName.setValue(formState.loginName)
                txtLoginPassword.setValue('')
                txtLastName.setValue(formState.lastName)
                txtFirstName.setValue(formState.firstName)
                txtSecondName.setValue(formState.secondName)
                txtAccessLevelID.setValue(formState.accessLevelID)
                txtAccessLevelName.setValue(res.data?.accessLevelName || '')
                txtIsLocked.setValue(formState.isLocked)

                itemsSites.setValue(res.data?.itemsSites || [])
                itemsAccess.setValue(res.data?.itemsAccess || [])
                savedFormSnapshotRef.current = createUserFormSnapshot(formState)
                isFormReadyRef.current = true
                isDisableBtnSave.setValue(true)
            }
        })
    }

    /** Функция генерации нового пароля пользователя */
    const generatePassword = (): string => {
        var length = 8, charset = "123456789@!*$fghjnrstvwz", retVal = ""

        for (var i = 0, n = charset.length; i < length; ++i) {
            retVal += charset.charAt(Math.floor(Math.random() * n))
        }

        return retVal
    }

    /** При выборе данных из справочника */
    const dictSelectorData = (dictName: DictNameType, data: {id: number, name: string, param1?: string, param2?: string, param3?: string, param4?: string, param5?: string, param6?: string}) => {
        if (!canEditCurrentCard()) return
        switch (dictName) {
            case 'accessLevels': {
                txtAccessLevelID.setValue(Number(data.id)) // Идентификатор должности
                txtAccessLevelName.setValue(data.name) // Наименование должности
                break
            }

            case 'accessSites': { // Доступые сайты
                actionPostUserAccessSites(currentCardID.value, Number(data.id)).then((res) => {
                    if (res.status === 200) {
                        hasSavedChangesRef.current = true
                        itemsSites.setValue([
                            ...itemsSites.value,

                            {
                                loginSiteID: res.data?.insertID || 0,
                                siteDbNameDescription: data.name,
                                siteLink: data.param1 || '',
                                siteDBName: data.param2 || ''
                            }
                        ])
                    } else {
                        notify && notifyOpen(res.message, 'warning', 1500, notify)
                    }
                })
                break
            }

            case 'accessSections': { // Доступные разделы
                actionPostUserAccessSections(currentCardID.value, Number(data.id)).then((res) => {
                    if (res.status === 200) {
                        hasSavedChangesRef.current = true
                        itemsAccess.setValue([
                            ...itemsAccess.value,

                            {
                                accessID: res.data?.insertID || 0,
                                accessSectionName: data.name,
                                accessSectionDescription: data.param1 || '',
                                accessSectionID: Number(data.id),
                                isRead: 0, isEdit: 0, isDelete: 0
                            }
                        ])
                    } else {
                        notify && notifyOpen(res.message, 'warning', 1500, notify)
                    }
                })
                break
            }
        }
    }

    /** Функция по активации анимации ошибок в указанных полях */
    const fieldsErrorAnumation = useFieldsErrorAnimation<IFieldsError>(fieldsError.setValue)

    //==================================================================================

    const closeCard = () => {
        modalConfirm.setValue({isOpen: false})

        if (hasSavedChangesRef.current) {
            handleButtonOk && handleButtonOk(true)
        } else {
            handleButtonCancel && handleButtonCancel()
        }
    }

    /** Открытие/закрытие окна подтверждения */
    const handleOpenModalConfirm = (isOpen: boolean, headerText: string, contentText: string, actionType: string, actionID: number = 0) => {
        if (isOpen && actionType !== "closeForm" && !canEditCurrentCard()) return
        modalConfirm.setValue({
            isOpen, headerText, contentText,
            cancelButtonText: actionType === 'closeForm' ? 'Продолжить редактирование' : undefined,
            okButtonText: actionType === 'closeForm' ? 'Закрыть без сохранения' : undefined,

            handleButtonCancel() {
                modalConfirm.setValue({isOpen: false})
            }, 
            
            handleButtonOk() {
                if (actionType !== "closeForm" && !canEditCurrentCard()) return
                switch (actionType) {
                    case "deleteUserSite": { // Удаление записи - Доступные сайты
                        actionDeleteUserAccessSites(actionID).then((res) => {
                            if (res.status === 200) {
                                hasSavedChangesRef.current = true
                                const updatedItems = itemsSites.value.filter(item => item.loginSiteID !== actionID)
                                itemsSites.setValue(updatedItems)
                            } else {
                                notify && notifyOpen(res.message, 'warning', 1500, notify)
                            }
                        })

                        modalConfirm.setValue({isOpen: false})
                        break
                    }

                    case 'deleteUserAccess': { // Удаление записи - Доступные разделы
                        actionDeleteUserAccessSections(actionID).then((res) => {
                            if (res.status === 200) {
                                hasSavedChangesRef.current = true
                                const updatedItems = itemsAccess.value.filter(item => item.accessID !== actionID)
                                itemsAccess.setValue(updatedItems)
                            } else {
                                notify && notifyOpen(res.message, 'warning', 1500, notify)
                            }
                        })

                        modalConfirm.setValue({isOpen: false})
                        break
                    }

                    case 'closeForm': { // Закрытие анкеты оператора
                        closeCard()
                        break
                    }
                }
            }
        })
    }

    /** Редактирование манипуляция по разделам для оператора */
    const handleOnChangeAccessSection = (id: number, type: string, e: boolean) => {
        if (!canEditCurrentCard()) return
        const isChecked: number = e ? 1 : 0
        
        actionPutUserAccessSectionsChecked(id, type, isChecked).then((res) => {
            if (res.status === 200) {
                hasSavedChangesRef.current = true
                const updatedItems = itemsAccess.value.map(item => {
                    if (item.accessID === id) {
                        return {
                            ...item,
                            [type === 'read' ? 'isRead' : type === 'edit' ? 'isEdit' : 'isDelete']: isChecked
                        }
                    }
                    return item
                })
                itemsAccess.setValue(updatedItems)
            } else {
                notify && notifyOpen(res.message, 'warning', 1500, notify)
            }
        })
    }

    /** Кнопка сохранить все данные во вкладке primary */
    const handleBtnSave = (isClosedModal: boolean) => {
        if (!canEditCurrentCard()) return
        const validateData = validFormData([ // Валидируем данные 
            {fieldName: 'loginName', value: txtLoginName.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Логин оператора обязательно для заполненения.'},
            {fieldName: 'accessLevel', value: txtAccessLevelName.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Должность оператора обязательно для заполненения.'},
            {fieldName: 'lastName', value: txtLastName.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Фамилия оператора обязательно для заполненения.'},
            {fieldName: 'firstName', value: txtFirstName.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Имя оператора обязательно для заполненения.'},
        ])

        for (const item of validateData) { // Выводим ошибки валидации
            fieldsErrorAnumation(item.fieldName as FieldsErrorType)
            notify && notifyOpen(item.message, 'warning', 2000, notify)
        }

        if ((currentCardMode.value === 'new' && txtLoginPassword.value.length < 8) || (txtLoginPassword.value.length > 0 && txtLoginPassword.value.length < 8)) {
            fieldsErrorAnumation('loginPassword' as FieldsErrorType)
            notify && notifyOpen('Пароль должен быть не менее 8 символов.', 'warning', 2000, notify)
            return
        }

        
        // Если все хорошо, то сохраняем данные
        if (validateData.length === 0) {
            switch (currentCardMode.value) {
                case 'new': {
                    const formState = getCurrentFormState()
                    saveGuard.runSave(() => actionPostUserCard({
                        loginName: formState.loginName,
                        loginPassword: formState.loginPassword,
                        lastName: formState.lastName,
                        firstName: formState.firstName,
                        secondName: formState.secondName,
                        accessLevelID: formState.accessLevelID,
                        isLocked: formState.isLocked
                    }), {isNew: true, getSavedID: response => response.data?.insertID}).then((res) => {
                        if (!res) return
                        if (res.status === 200) {
                            const savedUserID = Number(res.data?.insertID)
                            if (!Number.isSafeInteger(savedUserID) || savedUserID <= 0) {
                                notify && notifyOpen('Сервер не вернул идентификатор сохранённого оператора.', 'error', 2000, notify)
                                return
                            }

                            currentCardMode.setValue('edit')
                            currentCardID.setValue(savedUserID)
                            txtLoginPassword.setValue('')
                            hasSavedChangesRef.current = true
                            savedFormSnapshotRef.current = createUserFormSnapshot({...formState, loginPassword: ''})
                            isDisableBtnSave.setValue(true)

                            notify && notifyOpen('Оператор успешно создан', 'success', 1500, notify)
                            
                            if (isClosedModal) {
                                handleButtonOk && handleButtonOk(true)
                            } else {
                                loadCardData(savedUserID)
                            }
                        } else {
                            notify && notifyOpen(res.message, 'warning', 1500, notify)
                        }
                    })
                    break
                }

                case 'edit': {
                    const formState = getCurrentFormState()
                    saveGuard.runSave(() => actionPutUserCard(currentCardID.value, {
                        loginName: formState.loginName,
                        loginPassword: formState.loginPassword,
                        lastName: formState.lastName,
                        firstName: formState.firstName,
                        secondName: formState.secondName,
                        accessLevelID: formState.accessLevelID,
                        isLocked: formState.isLocked
                    }), {isNew: false, getSavedID: () => currentCardID.value}).then((res) => {
                        if (!res) return
                        if (res.status === 200) {
                            notify && notifyOpen('Данные успешно сохранены', 'success', 1500, notify)
                            txtLoginPassword.setValue('')
                            hasSavedChangesRef.current = true
                            savedFormSnapshotRef.current = createUserFormSnapshot({...formState, loginPassword: ''})
                            isDisableBtnSave.setValue(true)

                            if (isClosedModal) {
                                handleButtonOk && handleButtonOk(true)
                            } else {
                                loadCardData(currentCardID.value)
                            }
                        } else {
                            notify && notifyOpen(res.message, 'warning', 1500, notify)
                        }
                    })
                    break
                }
            }
        }
    }
    
    /** Кнопка закрытия окна */
    const handleBtnClose = () => {
        if (isLoadingCard || modalConfirm.value.isOpen) return

        if (isDisableBtnSave.value) {
            closeCard()
        } else {
            handleOpenModalConfirm(
                true, 
                'Сохранение формы', 
                '<b>Внимание!</b> Имеются не сохранённые данные!<br/><br/>Вы действительно хотите закрыть текущее окно, без сохранения?', 
                'closeForm'
            )
        }
    }

    // ================================================================================

    /** Свойства передаваемые в компоненту */
    const propsToComponent: ICardUserUI = {
        isOpen, isLoadingCard, canEdit,
        isLoadingCheckedSectionRead,
        isLoadingCheckedSectionEdit,
        isLoadingCheckedSectionDelete,

        currentCardID, currentCardMode,
        fieldsError,
        isDisableBtnSave: {...isDisableBtnSave, value: !canEdit || isDisableBtnSave.value || saveGuard.isBlocked},

        txtLoginName, txtLoginPassword,
        txtAccessLevelID, txtAccessLevelName, txtIsLocked,
        txtLastName, txtFirstName, txtSecondName,
        itemsSites, itemsAccess,
        
        generatePassword, dictSelectorData,
        handleOpenModalConfirm, 
        handleBtnClose, handleBtnSave, handleOnChangeAccessSection,
    }
    
    return <>
        <CardUserUI {...propsToComponent} />

        <ModalConfirm {...modalConfirm.value} />
    </>
}